/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
import * as StateManager from '../store/state/state-manager';
import * as ActionTypes from '../store/state/actions';
import { IsLoadingManagerInstance } from '../utils/loading-manager';
import Utils from '../utils/utils';
import type { Action } from '../store/state/state-manager';

/**
 * Provides the basic APIs to sequence different type of actions in the Constellation infrastructure.
 *
 * Actions are classified into 2 types blocking actions and deferred actions.
 *
 * For example: In an assignment, File Attachment is blocking action while finishAssignment is deferred action.
 * By using these apis, it is ensured that a deferred action is always fired when all blocking actions are completed.
 *
 */
class ActionsSequencer {
  MISSING_CONTEXT_PARAM = 'context is required parameter';
  INVALID_CONTEXT_DETAILS = 'Invalid Context';
  INVALID_CONTEXT_OPERATION = 'Invalid Operation on Given Context';

  /**
   * Dispatches queued actions on completion of currently running  action.Called from deRegisterBlockingAction,handleDeferredActionCompletion
   * @param context
   * For example: "app/modal_1"
   * @param contextDataObject data object for respective context fetched from redux
   *
   * @function
   * @private
   */
  schedulePendingActionOnCompletion(
    context: string,
    contextDataObject: {
      context_data: {
        blocking_action_counter: number;
        deferred_action_queue: Action[];
        is_deferred_action_in_progress: boolean;
      };
    }
  ) {
    const actionsToBeDispatched: Action[] = [];
    const blockingActionCounter = contextDataObject.context_data?.blocking_action_counter ?? 0;
    const deferredActionQueue = contextDataObject.context_data?.deferred_action_queue
      ? [...contextDataObject.context_data.deferred_action_queue]
      : [];
    let isDeferredActionInProgress = contextDataObject.context_data?.is_deferred_action_in_progress ?? false;
    if (blockingActionCounter <= 0) {
      if (isDeferredActionInProgress) {
        actionsToBeDispatched.push({
          type: ActionTypes.MERGE_DATA,
          payload: {
            context,
            data: {
              context_data: {
                is_deferred_action_in_progress: false
              }
            }
          }
        } as Action);
      } else if (deferredActionQueue.length > 0) {
        const actionToBeProcessedNext: any = deferredActionQueue.shift();
        isDeferredActionInProgress = !isDeferredActionInProgress;
        actionsToBeDispatched.push({
          type: ActionTypes.MERGE_DATA,
          payload: {
            context,
            data: {
              context_data: {
                deferred_action_queue: deferredActionQueue,
                blocking_action_counter: blockingActionCounter,
                is_deferred_action_in_progress: isDeferredActionInProgress
              }
            }
          }
        } as Action);
        if (actionToBeProcessedNext) {
          actionsToBeDispatched.push(actionToBeProcessedNext);
        }
      } else {
        return;
      }

      StateManager.dispatch(actionsToBeDispatched);
    }
  }

  /**
   * executes or queues deferred actions in the context of a container
   * @example <caption>In this example, the API tries to execute or queue the specified deferred action in the context of the 'app/primary_1' container and returns a promise containing the status of the deferred action</caption>
   * PCore.getActionsSequencer().executeOrQueueDeferredAction({
   * type: theType
   * payload: {
   *  context: app/primary_1,
   * //include other payload properties
   * }
   * }).then(successCallback).catch(failureCallback);
   * @param actionPayload The deferred action that must be executed or queued
   * @returns A promise associated with the action
   * <ul>
   * <li> The promise resolves successfully when the deferred action has been either executed or queued.
   * <li> The promise rejects with an error message when the deferred action has been neither executed nor queued.
   * </ul>
   *
   * @function
   */
  executeOrQueueDeferredAction(actionPayload: Action) {
    if (!actionPayload?.payload?.context) {
      return Promise.reject(new Error('Action Context is Mandatory Field.'));
    }
    const contextDataObject = StateManager.getDataObject(actionPayload?.payload?.context);
    if (contextDataObject) {
      const actionsToBeDispatched: Action[] = [];
      const blockingActionCounter = contextDataObject.context_data?.blocking_action_counter ?? 0;
      const deferredActionQueue = contextDataObject.context_data?.deferred_action_queue
        ? [...contextDataObject.context_data.deferred_action_queue]
        : [];
      let isDeferredActionInProgress = contextDataObject.context_data?.is_deferred_action_in_progress ?? false;
      if (deferredActionQueue.length <= 0 && !isDeferredActionInProgress) {
        if (blockingActionCounter <= 0) {
          actionsToBeDispatched.push(actionPayload);
          isDeferredActionInProgress = !isDeferredActionInProgress;
        } else {
          deferredActionQueue.push(actionPayload);
        }
        actionsToBeDispatched.unshift({
          type: ActionTypes.MERGE_DATA,
          payload: {
            context: actionPayload.payload.context,
            data: {
              context_data: {
                deferred_action_queue: deferredActionQueue,
                blocking_action_counter: blockingActionCounter,
                is_deferred_action_in_progress: isDeferredActionInProgress
              }
            }
          }
        });
        StateManager.dispatch(actionsToBeDispatched);
      }
      return Promise.resolve();
    }
    return Promise.reject(new Error(this.INVALID_CONTEXT_DETAILS));
  }

  /**
   * signals the completion of execution of the ongoing deferred action and schedules the next deferred action in queue for execution
   * @example <caption>In this example, the API tries to signal the completion of execution of the ongoing deferred action and returns a promise associated with the action.</caption>
   * PCore.getActionsSequencer().handleDeferredActionCompletion("app/primary_1").then(successCallback).catch(failureCallback);
   * @param context  The name of the context where the API is being called
   * @returns A promise associated with the action
   * Note:
   * <ul>
   * <li> The promise resolves successfully when the execution of the deferred action has been completed.
   * <li> The promise rejects with an error message when the execution of the deferred action has not been completed.
   * </ul>
   *
   * @function
   */
  handleDeferredActionCompletion(context: string) {
    if (!context) {
      return Promise.reject(new Error(this.MISSING_CONTEXT_PARAM));
    }
    const contextDataObject = StateManager.getDataObject(context);
    if (contextDataObject && contextDataObject.context_data?.is_deferred_action_in_progress === true) {
      StateManager.dispatch({
        type: ActionTypes.MERGE_DATA,
        payload: {
          context,
          data: {
            context_data: {
              is_deferred_action_in_progress: false
            }
          }
        }
      });
      this.schedulePendingActionOnCompletion(context, contextDataObject);
      return Promise.resolve();
    }
    return Promise.reject(new Error(this.INVALID_CONTEXT_DETAILS));
  }

  /**
   * Registers an ongoing blocking action in the context of a container
   * @example <caption>In this example, the API tries to register the ongoing blocking action in the context of the app/primary_1 container and returns a promise containing the registration status</caption>
   * PCore.getActionsSequencer().registerBlockingAction("app/primary_1").then(successCallback).catch(failureCallback);
   * @param context The name of the context where the APi si being called.
   * @returns A promise containing the registration status  of the ongoing blocking action
   * @function
   * @public
   */
  registerBlockingAction(context: string) {
    if (!context) {
      return Promise.reject(new Error(this.MISSING_CONTEXT_PARAM));
    }
    const contextDataObject = StateManager.getDataObject(context);
    if (contextDataObject) {
      StateManager.dispatch({
        type: ActionTypes.ALTER_BLOCKING_ACTION_COUNT,
        payload: {
          context,
          isInitiateBlockingAction: true
        }
      });
      return Promise.resolve();
    }
    return Promise.reject(new Error(this.INVALID_CONTEXT_DETAILS));
  }

  /**
   * De-registers an ongoing  blocking action in the context of a container when the blocking action is completed
   * @example <caption>In this example , the API tries to de-register the ongoing blocking action in the context of the app/primary_1 container and returns a promise containing the de-registration status</caption>
   * PCore.getActionsSequencer().deRegisterBlockingAction("app/primary_1").then(successCallback).catch(failureCallback);
   * @param context The name of the context where the API is being called.
   * @returns A promise containing the de-registration status  of the ongoing blocking action
   * @function
   * @public
   */
  deRegisterBlockingAction(context: string) {
    if (!context) {
      return Promise.reject(new Error(this.MISSING_CONTEXT_PARAM));
    }
    const contextDataObject = { ...StateManager.getDataObject(context) };
    contextDataObject.context_data = { ...contextDataObject.context_data };

    if (contextDataObject && contextDataObject.context_data?.blocking_action_counter > 0) {
      StateManager.dispatch({
        type: ActionTypes.ALTER_BLOCKING_ACTION_COUNT,
        payload: {
          context,
          isInitiateBlockingAction: false
        }
      });
      contextDataObject.context_data.blocking_action_counter -= 1;

      this.schedulePendingActionOnCompletion(context, contextDataObject);
      return Promise.resolve();
    }
    return Promise.reject(new Error(this.INVALID_CONTEXT_OPERATION));
  }

  /**
   * cancels queued deferred actions when an error occurs
   * @example <caption>In this example, the API cancels queued deferred actions when an error occurs.</caption>
   * PCore.getActionsSequencer().cancelDeferredActionsOnError("app/primary_1");
   * @param context The name of the context where the API is being called.
   * @function
   * @public
   */
  cancelDeferredActionsOnError(context: string) {
    if (!context) {
      return;
    }
    const contextDataObject = StateManager.getDataObject(context);
    if (contextDataObject) {
      const contextDataOnError = { ...contextDataObject };
      contextDataOnError.context_data = { ...contextDataOnError.context_data };
      if (
        contextDataOnError.context_data?.deferred_action_queue &&
        Utils.rejectPendingActions(contextDataOnError.context_data.deferred_action_queue)
      ) {
        contextDataOnError.context_data.deferred_action_queue = [];
      }
      if (contextDataOnError.context_data?.blocking_action_counter) {
        contextDataOnError.context_data.blocking_action_counter = 0;
      }
      if (contextDataOnError.context_data?.is_deferred_action_in_progress) {
        contextDataOnError.context_data.is_deferred_action_in_progress = false;
      }
      StateManager.dispatch({
        type: ActionTypes.MERGE_DATA,
        payload: {
          context,
          data: {
            ...contextDataOnError
          }
        }
      });
      IsLoadingManagerInstance.disableLoadingIndicator({ context });
    }
  }

  /**
   * checks if and deferred action is currently in progress
   * @param context
   * For example: "app/modal_1"
   * @param contextDataObject data object for respective context fetched from redux
   *
   * @function
   * @private
   */
  isDeferredActionInProgress(context: string) {
    if (!context) {
      return Promise.reject(new Error(this.MISSING_CONTEXT_PARAM));
    }
    const contextDataObject = StateManager.getDataObject(context);
    return !!(contextDataObject && contextDataObject.context_data?.is_deferred_action_in_progress === true);
  }
}

export default new ActionsSequencer();
